import { getCookie } from './Auth/getCookie';

// Latest-touch even after first-touch UTMs are locked in: ad platforms credit
// the most recent click, so a click ID on the URL replaces the stored one.
const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid'] as const;

type ClickIdKey = (typeof CLICK_ID_KEYS)[number];

const GCL_COOKIE_FOR_CLICK_ID: Partial<Record<ClickIdKey, string>> = {
  gclid: '_gcl_aw',
  gbraid: '_gcl_gb',
};

/** Conversion Linker value: `GCL.<unix seconds>.<click id>`. The last entry is newest. */
function parseGclCookieClickId(raw: string | null): string | undefined {
  if (!raw) return undefined;

  let value = raw;
  try {
    value = decodeURIComponent(raw);
  } catch {
    value = raw;
  }

  const marker = /GCL\.\d+\./g;
  let lastEnd = -1;
  for (let match = marker.exec(value); match; match = marker.exec(value)) {
    lastEnd = marker.lastIndex;
  }
  if (lastEnd < 0) return undefined;

  const clickId = value.slice(lastEnd).split('~')[0]?.trim();
  return clickId || undefined;
}

/** Meta `_fbc` value: `fb.<subdomainIndex>.<creationTime ms>.<fbclid>`. */
function parseFbcCookieClickId(raw: string | null): string | undefined {
  if (!raw) return undefined;

  let value = raw;
  try {
    value = decodeURIComponent(raw);
  } catch {
    value = raw;
  }

  const clickId = value.match(/^fb\.\d+\.\d+\.(.+)$/)?.[1]?.trim();
  return clickId || undefined;
}

function readPlatformClickId(key: ClickIdKey): string | undefined {
  if (key === 'fbclid') return parseFbcCookieClickId(getCookie('_fbc'));
  const cookieName = GCL_COOKIE_FOR_CLICK_ID[key];
  if (!cookieName) return undefined;
  return parseGclCookieClickId(getCookie(cookieName));
}

/** Platform cookies only fill a click ID that neither the URL nor `target` has. */
export function mergeLatestClickIds<T extends Partial<Record<ClickIdKey, string>>>(
  target: T,
  current: Partial<Record<ClickIdKey, string>>,
) {
  for (const key of CLICK_ID_KEYS) {
    const value = current[key] || target[key] || readPlatformClickId(key);
    if (value) target[key] = value;
  }
}
